/**
 * Pulls the human-readable reason out of a failed axios call.
 *
 * The API returns its reason under a few different keys depending on the
 * controller (`message`, `error`, sometimes a `details`/`errors` array), and
 * axios overwrites `err.message` with a useless "Request failed with status
 * code 409" — so the response body must always win over the Error object.
 * Reaching for the wrong key is what turns a precise
 * 'Purity "test 1" already exists for this metal type.' into "Operation failed".
 */
export const apiErrorMessage = (err, fallback = "Something went wrong") => {
  const data = err?.response?.data;

  // Some endpoints (and proxies) answer with a bare string body.
  if (typeof data === "string" && data.trim()) return data.trim();

  const direct = data?.message || data?.error || data?.msg;
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  // Validation-style payloads: { errors: [...] } / { details: [...] }
  const list = data?.errors || data?.details;
  if (Array.isArray(list) && list.length) {
    const texts = list
      .map((item) =>
        typeof item === "string"
          ? item
          : item?.message || item?.msg || item?.reason,
      )
      .filter(Boolean);
    if (texts.length) return texts.join(" · ");
  }

  // Request went out but nothing came back — offline, server down, timeout.
  if (err?.request && !err?.response) {
    return "Cannot reach the server. Check your connection and try again.";
  }

  // A locally thrown Error (e.g. success:false on a 200) carries a real message;
  // axios's generic status-code text does not.
  if (
    typeof err?.message === "string" &&
    err.message.trim() &&
    !/^Request failed with status code/i.test(err.message) &&
    err.message !== "Network Error"
  ) {
    return err.message.trim();
  }

  return fallback;
};

export default apiErrorMessage;
